<template>
  <nav
    class="relative flex flex-col whitespace-nowrap border-r border-gray-400 transition-[width] duration-200 ease-in-out motion-reduce:transition-none"
    :class="isCollapsed ? 'w-12' : 'w-sidenav'"
  >
    <button
      type="button"
      class="absolute -right-3 top-[92px] z-10 inline-flex items-center justify-center w-6 h-6 text-gray-600 hover:text-gray-900 bg-white border border-gray-400 rounded-full"
      :aria-label="isCollapsed ? 'メニューを開く' : 'メニューを閉じる'"
      :aria-expanded="!isCollapsed"
      @click="handleClickToggleButton"
    >
      <bs-icon
        name="chevron_down"
        class="w-3 h-auto transition-transform duration-200 ease-in-out motion-reduce:transition-none"
        :class="isCollapsed ? '-rotate-90' : 'rotate-90'"
      />
    </button>
    <span class="align-center flex justify-center mb-4 mt-3" :class="{ 'px-3': !isCollapsed }">
      <div v-if="!isCollapsed" class="py-4 w-full text-center rounded">
        <strong class="text-gray-700">{{ companyName }}</strong>
      </div>
      <div v-else class="inline-flex items-center justify-center w-8 h-8 bg-indigo-200 rounded-full">
        <span class="text-indigo-900 font-bold">{{ omitCompanyName }}</span>
      </div>
    </span>
    <side-nav-link
      v-if="role === 'store_admin' || role === 'store_staff'"
      to="/companies"
      icon="company"
      title="出店会社管理"
    />
    <side-nav-link
      v-if="role === 'store_admin' || role === 'store_staff'"
      to="/brands"
      icon="tenant"
      title="出店ブランド管理"
    />
    <side-nav-link
      v-if="role === 'store_admin' || role === 'store_staff'"
      to="/product-tags"
      icon="hashtag"
      title="タグ管理"
    />
    <side-nav-link to="/products" icon="shopping_bag" title="商品管理" />
    <side-nav-link to="/gift-wrapping-products" icon="gift" title="ギフト資材管理" />
    <side-nav-link to="/price-reservations" icon="price_reservations" title="商品価格変更予約" />
    <side-nav-link
      v-if="role === 'store_admin' || role === 'store_staff'"
      to="/orders"
      icon="shopping_cart"
      title="注文管理"
    />
    <side-nav-link
      v-if="role === 'store_admin' || role === 'store_staff'"
      to="/store/delivery"
      icon="shopping_bag_check"
      title="商品受渡管理"
    />
    <side-nav-link v-if="isDisplayBrandDirectShipping" to="/brand-direct-shippings" icon="truck" title="ブランド直送" />
    <side-nav-link
      v-if="role === 'store_admin' || role === 'store_staff'"
      to="/catalog"
      icon="tag"
      title="Webカタログ管理"
    />
    <side-nav-link
      v-if="role === 'store_admin' || role === 'store_staff'"
      to="/coupons"
      icon="coupon"
      title="クーポン管理"
    />
    <side-nav-link to="/reports" icon="chart" title="レポート" />
    <side-nav-link
      v-if="role === 'store_admin' || role === 'store_staff'"
      to="/shipment-days"
      icon="calendar"
      title="出荷可能日数管理"
    />
    <side-nav-link to="/manuals" icon="document" title="マニュアル" />
    <side-nav-link
      v-if="role === 'store_admin' || role === 'store_staff'"
      to="/short-urls"
      icon="link"
      title="短縮URL管理"
    />
    <side-nav-link
      v-if="role === 'store_admin' || role === 'tenant_admin'"
      to="/users"
      icon="users"
      title="ログインユーザー管理"
    />
    <bs-dropdown-menu
      class="mt-1 text-sm"
      :class="{ 'border-t-2': isCollapsed }"
      :collapsed="isCollapsed"
      label="マイメニュー"
    >
      <template #title>
        <div class="flex my-1 h-5">
          <bs-icon name="user_circle" class="relative h-auto" :class="isCollapsed ? 'left-0.5 w-5' : 'mr-3 w-4'" />
          <span v-if="!isCollapsed">マイメニュー</span>
        </div>
        <side-nav-tooltip v-if="isCollapsed">マイメニュー</side-nav-tooltip>
      </template>
      <template #body>
        <side-nav-link to="/mypage" icon="settings" title="アカウント設定" :class="{ 'my-1.5': !isCollapsed }" />
        <button
          class="group relative flex items-center px-3 py-3 w-full text-left text-gray-700 text-sm"
          :class="{ 'my-1.5': !isCollapsed }"
          :aria-label="isCollapsed ? 'ログアウト' : undefined"
          @click="handleClickLogoutButton"
        >
          <div class="flex h-5">
            <bs-icon name="logout" class="relative h-auto" :class="isCollapsed ? 'left-1 w-5' : 'left-0.5 mr-3 w-4'" />
            <span v-if="!isCollapsed">ログアウト</span>
          </div>
          <side-nav-tooltip v-if="isCollapsed">ログアウト</side-nav-tooltip>
        </button>
      </template>
    </bs-dropdown-menu>
  </nav>
</template>

<script lang="ts">
import { isBrandDirectShippingForAllBrand, isBrandDirectShippingForMyBrand } from "~/shared/role";
import { useMeStore } from "~/stores/me";
import { useSideNavStore } from "~/stores/sideNav";

const meStore = useMeStore();
export default defineComponent({
  computed: {
    role(): string {
      return meStore.role ?? "";
    },
    companyName(): string {
      return meStore.me?.companyName ?? "";
    },
    omitCompanyName(): string {
      if (!this.companyName) {
        return "";
      }

      return this.companyName.charAt(0);
    },
    isDisplayBrandDirectShipping(): boolean {
      return isBrandDirectShippingForMyBrand(this.role) || isBrandDirectShippingForAllBrand(this.role);
    },
    isCollapsed(): boolean {
      return useSideNavStore().isCollapsed;
    },
  },
  methods: {
    async handleClickLogoutButton() {
      await this.$auth.logout();
    },
    handleClickToggleButton() {
      useSideNavStore().toggle();
    },
  },
});
</script>
